import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, ToggleButton, ToggleButtonGroup, styled } from '@mui/material';
import { PageContainer, PageTitle } from '../../components/Common';
import { useLanguage } from '../../context/LanguageContext';
import { useLayout } from '../../global/Layout/Layout';
import { useLayoutPreferences } from '../../context/LayoutPreferencesContext';

const SettingSection = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(4),
}));

const SettingLabel = styled(Typography)(({ theme }) => ({
    fontSize: '14px',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    fontWeight: 500,
}));

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    width: '100%',
    '& .MuiToggleButton-root': {
        textTransform: 'none',
        fontSize: '13px',
        fontWeight: 500,
        padding: '8px 16px',
        border: `1px solid ${theme.palette.grey[400]}`,
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.background.paper,
        '&:hover': {
            backgroundColor: theme.palette.grey[50],
        },
        '&.Mui-selected': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            borderColor: theme.palette.primary.main,
            '&:hover': {
                backgroundColor: theme.palette.primary.dark,
            },
        },
    },
}));

type SidebarType = 'default' | 'icon';
type HeaderTheme = 'light' | 'dark';

const getStoredSidebarType = (): SidebarType => {
    const legacySidebarType = localStorage.getItem('sidebarType');
    if (legacySidebarType === 'icon' || legacySidebarType === 'default') {
        return legacySidebarType;
    }

    try {
        const savedPreferences = localStorage.getItem('layoutPreferences');
        const sidebarType = savedPreferences ? JSON.parse(savedPreferences).sidebarType : null;
        return sidebarType === 'icon' ? 'icon' : 'default';
    } catch {
        return 'default';
    }
};

const Settings = () => {
    const { t } = useLanguage();
    const { layoutMode, setLayoutMode, isMenuOpen, toggleMenu } = useLayout();
    const {
        topbarTheme,
        setTopbarTheme,
        setLayoutMode: setPreferredLayoutMode,
        setSidebarType: setPreferredSidebarType,
    } = useLayoutPreferences();
    
    const [sidebarType, setSidebarType] = useState<SidebarType>(() => getStoredSidebarType());

    const handleLayoutModeChange = (_: React.MouseEvent<HTMLElement>, newMode: 'vertical' | 'horizontal' | null) => {
        if (newMode !== null) {
            setLayoutMode(newMode);
            setPreferredLayoutMode(newMode);
            localStorage.setItem('layoutMode', newMode);
        }
    };

    const handleHeaderThemeChange = (_: React.MouseEvent<HTMLElement>, newTheme: HeaderTheme | null) => {
        if (newTheme !== null) {
            setTopbarTheme(newTheme);
        }
    };

    const handleSidebarTypeChange = (_: React.MouseEvent<HTMLElement>, newType: SidebarType | null) => {
        if (newType !== null) {
            setSidebarType(newType);
            setPreferredSidebarType(newType);
            localStorage.setItem('sidebarType', newType);
            
            if (newType === 'icon' && isMenuOpen) {
                toggleMenu();
            } else if (newType !== 'icon' && !isMenuOpen) {
                toggleMenu();
            }
        }
    };

    return (
        <PageContainer>
            <PageTitle>{t('pages.settings.title')}</PageTitle>
            
            <Card>
                <CardContent sx={{ p: 4, maxWidth: 500}}>
                    {/* Menu */}
                    <SettingSection>
                        <SettingLabel>{t('pages.settings.menu')}</SettingLabel>
                        <StyledToggleButtonGroup
                            value={layoutMode}
                            exclusive
                            onChange={handleLayoutModeChange}
                            fullWidth
                        >
                            <ToggleButton value="vertical">
                                {t('pages.settings.vertical')}
                            </ToggleButton>
                            <ToggleButton value="horizontal">
                                {t('pages.settings.horizontal')}
                            </ToggleButton>
                        </StyledToggleButtonGroup>
                    </SettingSection>

                    {/* Thème du header */}
                    <SettingSection>
                        <SettingLabel>{t('pages.settings.headerTheme')}</SettingLabel>
                        <StyledToggleButtonGroup
                            value={topbarTheme}
                            exclusive
                            onChange={handleHeaderThemeChange}
                            fullWidth
                        >
                            <ToggleButton value="light">
                                {t('pages.settings.light')}
                            </ToggleButton>
                            <ToggleButton value="dark">
                                {t('pages.settings.dark')}
                            </ToggleButton>
                        </StyledToggleButtonGroup>
                    </SettingSection>

                    {/* Type de Menu latérale gauche - uniquement en mode vertical */}
                    {layoutMode === 'vertical' && (
                        <SettingSection>
                            <SettingLabel>{t('pages.settings.sidebarType')}</SettingLabel>
                            <StyledToggleButtonGroup
                                value={sidebarType}
                                exclusive
                                onChange={handleSidebarTypeChange}
                                fullWidth
                            >
                                <ToggleButton value="default">
                                    {t('pages.settings.default')}
                                </ToggleButton>
                                <ToggleButton value="icon">
                                    {t('pages.settings.icon')}
                                </ToggleButton>
                            </StyledToggleButtonGroup>
                        </SettingSection>
                    )}
                </CardContent>
            </Card>
        </PageContainer>
    );
};

export default Settings;
